/* Excel hierarchy loader (TypeScript) */
/* global Excel */
import { Constants } from "../constants/constants";
const TARGET_SHEET = Constants.Practice_Site_MasterSheet_Name;
export async function loadPracticeSiteSheet() {
    return Excel.run(async (context) => {
        const sheets = context.workbook.worksheets;
        sheets.load("items/name");
        await context.sync();
        const match = sheets.items.find((s) => s.name.toLowerCase() === TARGET_SHEET.toLowerCase());
        if (!match) {
            console.warn(`Sheet '${TARGET_SHEET}' not found.`);
            return;
        }
        let values = [];
        try {
            const used = match.getUsedRange();
            used.load("values");
            await context.sync();
            values = used.values || [];
        }
        catch {
            const fb = match.getRange(Constants.Practice_Site_MasterSheet_Range);
            fb.load("values");
            await context.sync();
            values = fb.values || [];
        }
        if (!values.length) {
            console.warn("Hierarchy sheet empty.");
            return;
        }
        // Expecting header row with REQUIRED columns: PracticeID, PracticeName, SiteID, SiteName
        const header = values[0].map((h) => String(h || "").trim());
        const idx = {
            practiceId: header.findIndex((h) => /practice\s*id/i.test(h)),
            practiceName: header.findIndex((h) => /practice\s*name/i.test(h)),
            siteId: header.findIndex((h) => /site\s*id/i.test(h)),
            siteName: header.findIndex((h) => /site\s*name/i.test(h)),
        };
        if (idx.practiceId === -1 ||
            idx.practiceName === -1 ||
            idx.siteId === -1 ||
            idx.siteName === -1) {
            const missing = Object.entries(idx)
                .filter(([, v]) => v === -1)
                .map(([k]) => k)
                .join(", ");
            console.warn(`Missing required column(s): ${missing}`);
            return;
        }
        // Temporary aggregation structures:
        // pMap: practiceId -> { practice data + Map of its sites (for uniqueness & fast lookup during build) }
        // siteMap: siteId -> site (flat map for bySiteId lookup table later)
        const pMap = new Map();
        const siteMap = new Map();
        // Iterate all data rows (skip header at index 0)
        for (let r = 1; r < values.length; r++) {
            const row = values[r];
            if (!row)
                continue; // Skip completely empty row objects
            // Normalize and extract mandatory practice columns
            const pid = String(row[idx.practiceId] || "").trim();
            if (!pid)
                continue; // If practice ID missing, ignore the row (cannot group it)
            const pname = String(row[idx.practiceName] || "").trim();
            // Retrieve existing practice grouping or create a new one
            let p = pMap.get(pid);
            if (!p) {
                p = { id: pid, name: pname, sites: new Map() };
                pMap.set(pid, p); // Register new practice entry
            }
            // Sites are REQUIRED columns now (validated above). Attempt to add site if row has a SiteID value.
            const sid = String(row[idx.siteId] || "").trim();
            if (sid) {
                const siteName = String(row[idx.siteName] || "").trim();
                // Avoid duplicate site insertion for same practice/site combination
                if (!p.sites.get(sid)) {
                    const site = { id: sid, name: siteName, practiceId: pid };
                    p.sites.set(sid, site); // Attach to practice-specific site map
                    siteMap.set(sid, site); // Also record in global site map for quick bySiteId lookup table
                }
            }
        }
        const practices = Array.from(pMap.values()).map((p) => ({
            id: p.id,
            name: p.name,
            sites: Array.from(p.sites.values()),
        }));
        const byPracticeId = {};
        practices.forEach((pr) => (byPracticeId[pr.id] = pr));
        const bySiteId = {};
        siteMap.forEach((s, sid) => (bySiteId[sid] = s));
        // Publish the immutable snapshot of the hierarchy to the global cache for reuse.
        // Consumers can read window.__practiceHierarchy; if absent they trigger a load.
        window.__practiceHierarchy = {
            sourceSheet: match.name, // Original worksheet name used as source
            practices,
            byPracticeId,
            bySiteId,
        };
    });
}
